// Game mode definitions. Each mode wraps the engine with mode-specific goals + termination.

import { BOARD_WIDTH } from '../config';
import type { Board } from '../game/board';
import type { Engine } from '../game/engine';
import type { GameStats, ModeId, ModeResultDetails } from '../types';

export interface ModeRuntime {
  id: ModeId;
  name: string;
  short: string;
  description: string;
  /** Configure engine config before run. */
  engineConfig: () => Partial<import('../game/engine').EngineConfig>;
  /** Called every tick. Return true to terminate the run as finished. */
  shouldFinish: (engine: Engine) => boolean;
  /** Result rendering. */
  buildResult: (stats: GameStats, prevBest: number | null) => ModeResultDetails;
  /** Storage key for PBs (or null if no PB tracked). */
  pbKey: string | null;
  /** True if "lower is better" for PB. */
  pbLowerBetter: boolean;
  /** Number to extract from stats for PB comparison. */
  pbExtract: (s: GameStats) => number;
  /** Optional board initializer (e.g., adds garbage). */
  initBoard?: (board: Board) => void;
}

export const MARATHON: ModeRuntime = {
  id: 'marathon',
  name: 'marathon',
  short: '150 lines',
  description: 'clear 150 lines as gravity ramps up',
  engineConfig: () => ({ levelGravity: true, initialLevel: 1, noTopout: false }),
  shouldFinish: (e) => e.stats.lines >= 150,
  buildResult: (s, prev) => ({
    reasonText: s.lines >= 150 ? 'all 150 lines cleared' : 'topped out',
    primaryMetric: { label: 'score', value: s.score.toLocaleString() },
    secondaryMetrics: [
      { label: 'lines', value: `${s.lines}` },
      { label: 'level', value: `${s.level}` },
      { label: 'time', value: formatTime(s.elapsedMs) },
      { label: 'tetrises', value: `${s.tetrises}` },
    ],
    newRecord: prev != null && s.score > prev,
  }),
  pbKey: 'pb.marathon.score',
  pbLowerBetter: false,
  pbExtract: (s) => s.score,
};

export const SPRINT40: ModeRuntime = {
  id: 'sprint40',
  name: 'sprint',
  short: '40 lines',
  description: 'clear 40 lines as fast as you can',
  engineConfig: () => ({ levelGravity: false, initialLevel: 1, noTopout: false }),
  shouldFinish: (e) => e.stats.lines >= 40,
  buildResult: (s, prev) => ({
    reasonText: s.lines >= 40 ? '40 lines cleared' : 'topped out',
    primaryMetric: { label: 'time', value: formatTime(s.elapsedMs) },
    secondaryMetrics: [
      { label: 'pieces', value: `${s.pieces}` },
      { label: 'pps', value: (s.pieces / Math.max(1, s.elapsedMs / 1000)).toFixed(2) },
      { label: 'lines', value: `${s.lines}` },
      { label: 'score', value: s.score.toLocaleString() },
    ],
    newRecord: prev != null && s.lines >= 40 && s.elapsedMs < prev,
  }),
  pbKey: 'pb.sprint40.time',
  pbLowerBetter: true,
  pbExtract: (s) => (s.lines >= 40 ? s.elapsedMs : Number.POSITIVE_INFINITY),
};

export const ULTRA2MIN: ModeRuntime = {
  id: 'ultra2min',
  name: 'ultra',
  short: '2 minutes',
  description: 'highest score in 2 minutes',
  engineConfig: () => ({ levelGravity: false, initialLevel: 1, noTopout: false }),
  shouldFinish: (e) => e.stats.elapsedMs >= 120_000,
  buildResult: (s, prev) => ({
    reasonText: s.elapsedMs >= 120_000 ? 'two minutes elapsed' : 'topped out',
    primaryMetric: { label: 'score', value: s.score.toLocaleString() },
    secondaryMetrics: [
      { label: 'lines', value: `${s.lines}` },
      { label: 'tetrises', value: `${s.tetrises}` },
      { label: 't-spins', value: `${s.tspins}` },
      { label: 'pps', value: (s.pieces / Math.max(1, s.elapsedMs / 1000)).toFixed(2) },
    ],
    newRecord: prev != null && s.score > prev,
  }),
  pbKey: 'pb.ultra2min.score',
  pbLowerBetter: false,
  pbExtract: (s) => s.score,
};

export const ZEN: ModeRuntime = {
  id: 'zen',
  name: 'zen',
  short: 'no limit',
  description: 'no timer, no top-out. practice mode',
  engineConfig: () => ({ levelGravity: false, initialLevel: 1, noTopout: true }),
  shouldFinish: () => false,
  buildResult: (s) => ({
    reasonText: 'session ended by player',
    primaryMetric: { label: 'lines', value: `${s.lines}` },
    secondaryMetrics: [
      { label: 'score', value: s.score.toLocaleString() },
      { label: 'time', value: formatTime(s.elapsedMs) },
      { label: 'tetrises', value: `${s.tetrises}` },
      { label: 'pieces', value: `${s.pieces}` },
    ],
  }),
  pbKey: null,
  pbLowerBetter: false,
  pbExtract: () => 0,
};

export const MASTER: ModeRuntime = {
  id: 'master',
  name: 'master',
  short: 'level 15',
  description: 'start at level 15 with brutal gravity',
  engineConfig: () => ({ levelGravity: true, initialLevel: 15, noTopout: false }),
  shouldFinish: () => false,
  buildResult: (s, prev) => ({
    reasonText: 'topped out',
    primaryMetric: { label: 'score', value: s.score.toLocaleString() },
    secondaryMetrics: [
      { label: 'lines', value: `${s.lines}` },
      { label: 'level', value: `${s.level}` },
      { label: 'time', value: formatTime(s.elapsedMs) },
      { label: 't-spins', value: `${s.tspins}` },
    ],
    newRecord: prev != null && s.score > prev,
  }),
  pbKey: 'pb.master.score',
  pbLowerBetter: false,
  pbExtract: (s) => s.score,
};

const CHEESE_ROWS = 10;
// Mode ID stays 'cheese' so existing PB keys + achievement state survive
// the rename. Display name is 'garbage' across the UI / help / results.
export const CHEESE: ModeRuntime = {
  id: 'cheese',
  name: 'garbage',
  short: `${CHEESE_ROWS} garbage`,
  description: `dig through ${CHEESE_ROWS} pre-filled garbage rows`,
  // Garbage ramps gravity with level so the pressure escalates as you
  // dig — otherwise the "level up" event fires without any actual speed
  // change and the run gets staler the longer it goes.
  engineConfig: () => ({ levelGravity: true, initialLevel: 1, noTopout: false }),
  shouldFinish: (e) => {
    // Finish when no garbage cells remain anywhere.
    for (let y = 0; y < 40; y++) {
      for (let x = 0; x < BOARD_WIDTH; x++) {
        if (e.board.at(x, y) === 'G') return false;
      }
    }
    return true;
  },
  buildResult: (s, prev) => {
    // Detect whether all garbage was actually cleared based on lines stat (>= ~18) plus
    // we can't know the topout reason here, but the App may overwrite reasonText.
    return {
      reasonText: 'garbage cleared',
      primaryMetric: { label: 'time', value: formatTime(s.elapsedMs) },
      secondaryMetrics: [
        { label: 'pieces', value: `${s.pieces}` },
        { label: 'pps', value: (s.pieces / Math.max(1, s.elapsedMs / 1000)).toFixed(2) },
        { label: 'lines', value: `${s.lines}` },
        { label: 'level', value: `${s.level}` },
      ],
      newRecord: prev != null && s.elapsedMs < prev,
    };
  },
  pbKey: 'pb.cheese.time',
  pbLowerBetter: true,
  pbExtract: (s) => s.elapsedMs,
  initBoard: (board) => {
    // CHEESE_ROWS garbage rows from the bottom, each with one random hole.
    // Holes never align across consecutive rows so a single piece can't dig
    // more than one row at a time.
    let seed = (Date.now() ^ 0x5a17b00b) >>> 0;
    const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 0x100000000; };
    let lastHole = -1;
    for (let r = 0; r < CHEESE_ROWS; r++) {
      let hole = Math.floor(rand() * BOARD_WIDTH);
      while (hole === lastHole) hole = Math.floor(rand() * BOARD_WIDTH);
      lastHole = hole;
      const y = 39 - r; // bottom-most rows
      for (let x = 0; x < BOARD_WIDTH; x++) {
        if (x !== hole) board.set(x, y, 'G');
      }
    }
  },
};

export const ALL_MODES: ReadonlyArray<ModeRuntime> = [MARATHON, SPRINT40, ULTRA2MIN, CHEESE, MASTER, ZEN];

export function getMode(id: ModeId): ModeRuntime {
  const m = ALL_MODES.find((m) => m.id === id);
  if (!m) throw new Error(`unknown mode: ${id}`);
  return m;
}

export function formatTime(ms: number): string {
  const totalCentiseconds = Math.floor(ms / 10);
  const cs = totalCentiseconds % 100;
  const totalSeconds = Math.floor(totalCentiseconds / 100);
  const s = totalSeconds % 60;
  const m = Math.floor(totalSeconds / 60);
  return `${m}:${s.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
}
